import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  loginUser,
  logoutUser,
  checkAuthStatus,
  clearError,
  selectAuth,
  selectUser,
  selectIsAuthenticated,
  selectIsLoading,
  selectAuthError,
} from '../store/slices/authSlice';
import type { LoginCredentials } from '../services/api';

// Custom hook for authentication
export const useAuth = () => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector(selectAuth);
  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectAuthError);

  // Login function
  const login = useCallback(
    async (credentials: LoginCredentials) => {
      const result = await dispatch(loginUser(credentials));
      if (loginUser.rejected.match(result)) {
        throw new Error(result.payload as string);
      }
      return result.payload;
    },
    [dispatch]
  );

  // Logout function
  const logout = useCallback(async () => {
    await dispatch(logoutUser());
  }, [dispatch]);

  // Check auth status function
  const checkAuth = useCallback(async () => {
    await dispatch(checkAuthStatus());
  }, [dispatch]);

  // Clear error function
  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,
    auth,

    // Actions
    login,
    logout,
    checkAuth,
    clearError: clearAuthError,
  };
};
